import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import {
  AGREEMENT_TYPE_LABELS,
  TICKET_ORIGINS,
  type Agreement,
  type Invoice,
  type Opportunity,
  type Project,
  type Ticket,
  type TicketOrigin,
  type TimeEntry,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { useAuth } from '../../auth/authContext.js';
import { useAgreements, useQuickAddPayAsYouGo } from '../agreements/api.js';
import { AGREEMENT_STATUS_LABELS, AGREEMENT_STATUS_TONES } from '../agreements/status.js';
import { useInvoices } from '../invoices/api.js';
import {
  INVOICE_SOURCE_LABELS,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_TONES,
} from '../invoices/status.js';
import { useOpportunities } from '../opportunities/api.js';
import { OpportunityFormModal } from '../opportunities/OpportunityFormModal.js';
import {
  OPPORTUNITY_STAGE_LABELS,
  OPPORTUNITY_STAGE_TONES,
  OPPORTUNITY_STATUS_LABELS,
  OPPORTUNITY_STATUS_TONES,
} from '../opportunities/status.js';
import { useProjects } from '../projects/api.js';
import { PROJECT_STATUS_LABELS, PROJECT_STATUS_TONES } from '../projects/status.js';
import { useTicketStatusOptions, useTickets } from '../tickets/api.js';
import { ticketStatusTone } from '../tickets/status.js';
import { ticketDisplayId } from '../tickets/reference.js';
import { useTimeEntries } from '../time-entries/api.js';
import {
  BILLING_STATUS_FILTERS,
  billingStatusOf,
  matchesBillingStatusFilter,
} from '../time-entries/billing-status.js';
import { formatDate } from '../../utils/date.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const HEAD_CLASS = 'px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500';
const CELL_CLASS = 'px-3 py-2 text-sm text-slate-700';

function Table({
  head,
  children,
  colSpan,
  ...state
}: {
  head: string[];
  children: React.ReactNode;
  colSpan: number;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  isEmpty: boolean;
  emptyTitle: string;
  onRetry?: () => void;
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full border-collapse">
        <thead className="border-b border-slate-200 bg-slate-50">
          <tr>
            {head.map((h) => (
              <th key={h} className={HEAD_CLASS}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <DataState colSpan={colSpan} {...state}>
            {children}
          </DataState>
        </tbody>
      </table>
    </div>
  );
}

export function CustomerAgreementsTab({ customerId }: { customerId: string }): JSX.Element {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can('agreements', 'edit');
  const query = useAgreements({ page: 1, pageSize: 200, customerId });
  const rows = query.data?.data ?? [];
  const quickAdd = useQuickAddPayAsYouGo();

  // Support coverage is a single active tickets OR managed services agreement, so the
  // quick-add is only offered when the customer has neither. The backend re-checks.
  const hasSupportCoverage = rows.some(
    (a: Agreement) =>
      a.status === 'active' && (a.type === 'tickets' || a.type === 'managed_services'),
  );
  const quickAddError = quickAdd.isError
    ? quickAdd.error instanceof ApiError
      ? quickAdd.error.message
      : 'Could not create the pay-as-you-go agreement.'
    : null;

  return (
    <div className="flex flex-col gap-3">
      {canEdit && !query.isLoading && !hasSupportCoverage ? (
        <div className="flex flex-col items-end gap-1">
          <Button
            size="sm"
            variant="secondary"
            isLoading={quickAdd.isPending}
            onClick={() => quickAdd.mutate({ customerId })}
          >
            Quick add pay-as-you-go
          </Button>
          <p className="text-xs text-slate-500">
            Creates an active tickets agreement starting the first of this month at default
            rates, and sets it as the default support agreement.
          </p>
          {quickAddError ? <p className="text-xs text-rose-600">{quickAddError}</p> : null}
        </div>
      ) : null}
      <Table
        head={['Name', 'Type', 'Status', 'Start', 'End']}
        colSpan={5}
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={rows.length === 0}
        emptyTitle="No agreements for this customer."
        onRetry={() => void query.refetch()}
      >
        {rows.map((a: Agreement) => (
          <tr
            key={a.id}
            onClick={() =>
              navigate(`/agreements/${a.id}`, {
                state: { from: 'customer', customerId, tab: 'agreements' },
              })
            }
            className="cursor-pointer hover:bg-slate-50"
          >
            <td className={`${CELL_CLASS} font-medium text-slate-900`}>{a.name}</td>
            <td className={CELL_CLASS}>{AGREEMENT_TYPE_LABELS[a.type]}</td>
            <td className={CELL_CLASS}>
              <Badge tone={AGREEMENT_STATUS_TONES[a.status]}>
                {AGREEMENT_STATUS_LABELS[a.status]}
              </Badge>
            </td>
            <td className={CELL_CLASS}>{formatDate(a.startDate)}</td>
            <td className={CELL_CLASS}>{formatDate(a.endDate)}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

export function CustomerProjectsTab({ customerId }: { customerId: string }): JSX.Element {
  const navigate = useNavigate();
  const query = useProjects({ page: 1, pageSize: 200, customerId });
  const rows = query.data?.data ?? [];
  return (
    <Table
      head={['#', 'Name', 'Status', 'Budget hrs', 'Actual hrs']}
      colSpan={5}
      isLoading={query.isLoading}
      isError={query.isError}
      error={query.error}
      isEmpty={rows.length === 0}
      emptyTitle="No projects for this customer."
      onRetry={() => void query.refetch()}
    >
      {rows.map((p: Project) => (
        <tr
          key={p.id}
          onClick={() =>
            navigate(`/projects/${p.id}`, {
              state: { from: 'customer', customerId, tab: 'projects' },
            })
          }
          className="cursor-pointer hover:bg-slate-50"
        >
          <td className={`${CELL_CLASS} tabular-nums text-slate-500`}>#{p.number}</td>
          <td className={`${CELL_CLASS} font-medium text-slate-900`}>{p.name}</td>
          <td className={CELL_CLASS}>
            <Badge tone={PROJECT_STATUS_TONES[p.status]}>{PROJECT_STATUS_LABELS[p.status]}</Badge>
          </td>
          <td className={`${CELL_CLASS} tabular-nums`}>{p.budgetHours}</td>
          <td className={`${CELL_CLASS} tabular-nums`}>{p.actualHours}</td>
        </tr>
      ))}
    </Table>
  );
}

export function CustomerTicketsTab({ customerId }: { customerId: string }): JSX.Element {
  const navigate = useNavigate();
  const [origin, setOrigin] = useState<TicketOrigin | ''>('');
  // Status filter: 'open' = any not-closed status (default), '' = all, otherwise a status id.
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const isOpenFilter = statusFilter === 'open';
  const query = useTickets({
    page: 1,
    pageSize: 200,
    customerId,
    origin: origin || undefined,
    open: isOpenFilter ? true : undefined,
    statusId: isOpenFilter || statusFilter === '' ? undefined : statusFilter,
  });
  const statusesQuery = useTicketStatusOptions();
  const statusOptions = statusesQuery.data ?? [];
  const statusName = (id: string): string => statusOptions.find((s) => s.id === id)?.name ?? '—';
  const rows = query.data?.data ?? [];

  const FILTER_CLASS =
    'rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={origin}
          onChange={(e) => setOrigin(e.target.value as TicketOrigin | '')}
          className={FILTER_CLASS}
          aria-label="Filter by ticket type"
        >
          <option value="">All types</option>
          {TICKET_ORIGINS.map((o) => (
            <option key={o} value={o}>
              {o === 'project' ? 'Project' : 'Support'}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={FILTER_CLASS}
          aria-label="Filter by status"
        >
          <option value="open">Open</option>
          <option value="">All statuses</option>
          {statusOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <Table
        head={['#', 'Title', 'Type', 'Status', 'Priority', 'Hours', 'LTT']}
        colSpan={7}
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={rows.length === 0}
        emptyTitle="No tickets for this customer."
        onRetry={() => void query.refetch()}
      >
        {rows.map((t: Ticket) => (
          <tr
            key={t.id}
            onClick={() =>
              navigate(`/tickets/${t.id}?returnCustomerId=${customerId}&returnTab=tickets`, {
                state: { from: 'customer', customerId, tab: 'tickets' },
              })
            }
            className="cursor-pointer hover:bg-slate-50"
          >
            <td className={`${CELL_CLASS} tabular-nums text-slate-500`}>{ticketDisplayId(t)}</td>
            <td className={`${CELL_CLASS} font-medium text-slate-900`}>{t.title}</td>
            <td className={CELL_CLASS}>
              <Badge tone={t.origin === 'support' ? 'amber' : 'blue'}>
                {t.origin === 'support' ? 'Support' : 'Project'}
              </Badge>
            </td>
            <td className={CELL_CLASS}>
              {statusOptions.find((s) => s.id === t.statusId)?.name ? (
                <Badge tone={ticketStatusTone(statusName(t.statusId))}>
                  {statusName(t.statusId)}
                </Badge>
              ) : (
                '—'
              )}
            </td>
            <td className={`${CELL_CLASS} capitalize`}>{t.priority}</td>
            <td className={`${CELL_CLASS} tabular-nums`}>{t.actualHours}</td>
            <td className={`${CELL_CLASS} text-slate-600`}>{formatDate(t.latestTimeDate)}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

export function CustomerTimeEntriesTab({ customerId }: { customerId: string }): JSX.Element {
  const navigate = useNavigate();
  const query = useTimeEntries({
    page: 1,
    pageSize: 200,
    customerId,
    sortBy: 'workDate',
    sortDir: 'desc',
  });
  const rows = query.data?.data ?? [];

  // Filters: a text search across every displayed field + a billing-status dropdown.
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((e) => {
      if (!matchesBillingStatusFilter(e, statusFilter)) return false;
      if (!term) return true;
      const status = billingStatusOf(e);
      const haystack = [
        formatDate(e.workDate),
        `${e.hours}`,
        e.memberName ?? '',
        e.notes ?? '',
        status.label,
        status.detail ?? '',
        e.invoiceNumber ?? '',
        e.externalInvoiceRef ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, search, statusFilter]);

  return (
    <div>
      {rows.length > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by date, member, notes, status…"
            className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-300 px-2.5 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            {BILLING_STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <Table
        head={['Date', 'Member', 'Hours', 'Status', 'Notes']}
        colSpan={5}
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={filtered.length === 0}
        emptyTitle={
          rows.length === 0
            ? 'No time entries for this customer.'
            : 'No time entries match the filter.'
        }
        onRetry={() => void query.refetch()}
      >
        {filtered.map((e: TimeEntry) => {
          const status = billingStatusOf(e);
          return (
            <tr
              key={e.id}
              onClick={() =>
                navigate(`/time-entries/${e.id}?returnCustomerId=${customerId}&returnTab=time`, {
                  state: { from: 'customer', customerId, tab: 'time' },
                })
              }
              className="cursor-pointer hover:bg-slate-50"
            >
              <td className={`${CELL_CLASS} text-slate-600`}>{formatDate(e.workDate)}</td>
              <td className={`${CELL_CLASS} text-slate-600`}>{e.memberName ?? '—'}</td>
              <td className={`${CELL_CLASS} tabular-nums`}>{e.hours}</td>
              <td className={CELL_CLASS}>
                <Badge tone={status.tone}>
                  {status.detail ? `${status.label} · ${status.detail}` : status.label}
                </Badge>
              </td>
              <td className={`${CELL_CLASS} text-slate-500`}>{e.notes ?? '—'}</td>
            </tr>
          );
        })}
      </Table>
    </div>
  );
}

export function CustomerOpportunitiesTab({ customerId }: { customerId: string }): JSX.Element {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can('sales', 'edit');
  const [createOpen, setCreateOpen] = useState(false);
  const query = useOpportunities({ page: 1, pageSize: 200, customerId });
  const rows = query.data?.data ?? [];
  return (
    <div className="flex flex-col gap-3">
      {canEdit ? (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            New opportunity
          </Button>
        </div>
      ) : null}
      <Table
        head={['Name', 'Stage', 'Status', 'Close date']}
        colSpan={4}
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={rows.length === 0}
        emptyTitle="No opportunities for this customer."
        onRetry={() => void query.refetch()}
      >
        {rows.map((o: Opportunity) => (
          <tr
            key={o.id}
            onClick={() =>
              navigate(
                `/sales/opportunities/${o.id}?returnCustomerId=${customerId}&returnTab=opportunities`,
                { state: { from: 'customer', customerId, tab: 'opportunities' } },
              )
            }
            className="cursor-pointer hover:bg-slate-50"
          >
            <td className={`${CELL_CLASS} font-medium text-slate-900`}>{o.name}</td>
            <td className={CELL_CLASS}>
              <Badge tone={OPPORTUNITY_STAGE_TONES[o.stage]}>
                {OPPORTUNITY_STAGE_LABELS[o.stage]}
              </Badge>
            </td>
            <td className={CELL_CLASS}>
              <Badge tone={OPPORTUNITY_STATUS_TONES[o.status]}>
                {OPPORTUNITY_STATUS_LABELS[o.status]}
              </Badge>
            </td>
            <td className={CELL_CLASS}>{formatDate(o.closeDate)}</td>
          </tr>
        ))}
      </Table>
      {createOpen ? (
        <OpportunityFormModal
          isOpen
          opportunity={null}
          initialCustomerId={customerId}
          onClose={() => {
            setCreateOpen(false);
            void query.refetch();
          }}
        />
      ) : null}
    </div>
  );
}

export function CustomerInvoicesTab({ customerId }: { customerId: string }): JSX.Element {
  const navigate = useNavigate();
  const query = useInvoices({
    page: 1,
    pageSize: 200,
    customerId,
    sortBy: 'issueDate',
    sortDir: 'desc',
  });
  const rows = query.data?.data ?? [];
  return (
    <Table
      head={['Number', 'Kind', 'Status', 'Issued', 'Due', 'Total']}
      colSpan={6}
      isLoading={query.isLoading}
      isError={query.isError}
      error={query.error}
      isEmpty={rows.length === 0}
      emptyTitle="No invoices for this customer."
      onRetry={() => void query.refetch()}
    >
      {rows.map((inv: Invoice) => (
        <tr
          key={inv.id}
          onClick={() =>
            navigate(`/invoices/${inv.id}`, {
              state: { from: 'customer', customerId, tab: 'invoices' },
            })
          }
          className="cursor-pointer hover:bg-slate-50"
        >
          <td className={`${CELL_CLASS} font-medium text-slate-900`}>{inv.number}</td>
          <td className={CELL_CLASS}>{INVOICE_SOURCE_LABELS[inv.source]}</td>
          <td className={CELL_CLASS}>
            <Badge tone={INVOICE_STATUS_TONES[inv.status]}>
              {INVOICE_STATUS_LABELS[inv.status]}
            </Badge>
          </td>
          <td className={CELL_CLASS}>{formatDate(inv.issueDate)}</td>
          <td className={CELL_CLASS}>{formatDate(inv.dueDate)}</td>
          <td className={`${CELL_CLASS} tabular-nums`}>{currency.format(Number(inv.total))}</td>
        </tr>
      ))}
    </Table>
  );
}
